import { Navigate, useLocation } from "react-router-dom";

import { useDateRangeParams } from "@/common/hooks/use-date-range-params";

import {
  getAvailableFilters,
  getFilterByDateRange,
  getFilterParams,
  getInitialFilter,
} from "./quick-filters.utils";

type Props = {
  filters: readonly { label: string }[];
  activeFilter?: { label: string } | { label: string }[];
  onClick: (index: number) => void;
};

const QuickFilters = ({
  activeFilter,
  children,
  filters,
  onClick,
}: React.PropsWithChildren<Props>) => {
  const isCurrent = (filter: { label: string }) =>
    (Array.isArray(activeFilter) ? activeFilter : [activeFilter]).find(
      (f) => f?.label === filter.label,
    );

  return (
    <ul className="inline-flex shrink-0 gap-1.5" role="navigation">
      {children ? <li className="inline-block shrink-0">{children}</li> : null}
      {filters.map((filter, i) => (
        <li
          aria-current={isCurrent(filter) ? "page" : false}
          className="inline-block shrink-0"
          key={filter.label}
        >
          <button
            className={`border-can-forest-teal text-can-forest-teal self-center rounded border px-1.5 py-0.5 text-xs ${
              isCurrent(filter)
                ? "from-can-tranquil-azure to-can-mystic-aqua border-transparent bg-linear-to-r font-bold text-white"
                : ""
            }`}
            onClick={() => onClick(i)}
          >
            {filter.label}
          </button>
        </li>
      ))}
    </ul>
  );
};

const Params = ({ children }: React.PropsWithChildren) => {
  const searchParams = useDateRangeParams();
  const { pathname, search } = useLocation();

  if (
    !searchParams ||
    !getFilterByDateRange(getAvailableFilters(), getFilterParams(searchParams))
  ) {
    const params = new URLSearchParams(search);
    Object.entries(getInitialFilter()).forEach(([key, value]) => {
      params.set(key, value);
    });

    return <Navigate replace to={`${pathname}?${params.toString()}`} />;
  }

  return <>{children}</>;
};

export {
  getFilterByDateRange,
  getFilterParams,
  getInitialFilter as getInitialDateFilter,
  Params,
  QuickFilters,
};
